import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Input, Modal, Select } from '@react-apps/design-system';
import type {
  Institution,
  SimilarInstitution,
  Uf,
} from '../../domain/entities/content.entity';
import { UFS } from '../../domain/entities/content.entity';
import {
  useCreateInstitution,
  useInstitutions,
  useUpdateInstitution,
} from '../hooks/use-institutions';
import { Switch } from './Switch';
import { Mono } from '../display';
import { getApiErrorMessage, readErrorCode, readErrorDetail } from '../display';

const UF_OPTIONS = UFS.map((uf) => ({ value: uf, label: uf }));

/** Tempo entre a última tecla na sigla e a consulta do painel de duplicata. */
const DUPLICATE_LOOKUP_DEBOUNCE_MS = 400;

interface InstitutionFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Quando presente, o modal edita; senão, cria. */
  institution?: Institution | null;
  /** Pré-preenchimento vindo do deep link da importação (`?new=1&...`). */
  prefill?: { initials?: string; uf?: string; name?: string };
  /**
   * "Abrir/restaurar essa" — a linha que o painel de duplicata listou ou que o
   * 409 apontou. Recebe a sigla junto porque a linha pode não estar na página
   * atual da listagem: com a sigla, o painel sabe para onde levar.
   */
  onOpenExisting?: (institutionId: string, initials: string) => void;
}

/**
 * Cria (sem `institution`) ou edita (com `institution`) uma instituição.
 *
 * Duas peças que não são enfeite:
 *
 * - **Preview ao vivo do rótulo**, na mesma gramática do
 *   `formatInstitutionLabel`: é literalmente a célula que a planilha de
 *   importação precisa na coluna `institution`, e o par (sigla, nome) tem de
 *   bater exato — ver `csv-format.md`.
 * - **Painel de duplicata** ao digitar uma sigla que já existe. Ele não bloqueia
 *   o submit — quem decide é o backend. A chave é sigla + UF + nome, sem caixa
 *   e sem acento, e há duas recusas com saídas diferentes: a chave idêntica é
 *   `INSTITUTION_DUPLICATE` e vem com o id da linha existente (daí o botão
 *   "abrir a instituição que já existe"); a mesma sigla + UF com outro nome é
 *   `INSTITUTION_SIMILAR_EXISTS` e vem com as parecidas, e aí a saída é abrir
 *   uma delas ou confirmar que é outra instituição ("cadastrar mesmo assim").
 *   A lista existe para essa decisão ser tomada com os dados na frente.
 */
export function InstitutionFormModal({
  open,
  onClose,
  institution,
  prefill,
  onOpenExisting,
}: InstitutionFormModalProps) {
  const isEdit = !!institution;
  const [name, setName] = useState('');
  const [initials, setInitials] = useState('');
  const [uf, setUf] = useState<Uf | ''>('');
  const [isInHouse, setIsInHouse] = useState(false);

  const create = useCreateInstitution();
  const update = useUpdateInstitution();
  const mutation = isEdit ? update : create;

  // Pelos valores do `prefill`, não pelo objeto: o painel monta um novo a cada
  // render, e o formulário era apagado sempre que ele re-renderizava — o nome
  // digitado sumia, por exemplo, entre o 409 de parecida e a confirmação.
  useEffect(() => {
    if (!open) return;
    setName(institution?.name ?? prefill?.name ?? '');
    setInitials(
      (institution?.initials ?? prefill?.initials ?? '').toUpperCase(),
    );
    setUf(pickUf(institution?.uf ?? prefill?.uf));
    setIsInHouse(institution?.isInHouse ?? false);
  }, [open, institution, prefill?.name, prefill?.initials, prefill?.uf]);

  // --- Painel de duplicata (só na criação) ---
  const [debouncedInitials, setDebouncedInitials] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedInitials(initials.trim()),
      DUPLICATE_LOOKUP_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [initials]);

  const lookupInitials = isEdit ? '' : debouncedInitials;
  const { data: sameInitials } = useInstitutions(
    {
      page: 1,
      perPage: 20,
      search: lookupInitials,
      // As arquivadas também interessam: restaurar uma é o caminho quando ela é
      // a mesma instituição, em vez de cadastrar uma segunda.
      includeDeleted: true,
    },
    // Só na criação, e só depois de haver sigla: nada de buscar a lista inteira
    // a cada abertura do modal.
    { enabled: !!lookupInitials },
  );

  const candidates = useMemo(() => {
    if (!lookupInitials) return [];
    const hits = (sameInitials?.items ?? []).filter(
      (i) => i.initials.toLowerCase() === lookupInitials.toLowerCase(),
    );
    // As da UF escolhida primeiro; as de outra UF continuam na lista, porque
    // esconder a linha de outra UF seria esconder o caso mais confuso.
    return [...hits].sort((a, b) => Number(b.uf === uf) - Number(a.uf === uf));
  }, [sameInitials, lookupInitials, uf]);

  const close = () => {
    mutation.reset();
    onClose();
  };

  // A recusa vale para a chave que o back avaliou. Mexer na sigla, na UF ou no
  // nome a descarta: senão o "mesmo assim" confirmaria uma chave que ninguém
  // conferiu, e o "abrir a que existe" levaria para a sigla nova.
  const editKey =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      if (mutation.error != null) mutation.reset();
      set(value);
    };
  const setKeyName = editKey(setName);
  const setKeyInitials = editKey(setInitials);
  const setKeyUf = editKey(setUf);

  const trimmedName = name.trim();
  const trimmedInitials = initials.trim();

  const submit = (allowSimilar: boolean) => {
    if (!trimmedName || !trimmedInitials || !uf) return;
    const input = {
      name: trimmedName,
      initials: trimmedInitials,
      uf,
      isInHouse,
      ...(allowSimilar && { allowSimilar }),
    };
    if (isEdit) {
      update.mutate(
        { institutionId: institution.id, input },
        { onSuccess: close },
      );
    } else {
      create.mutate(input, { onSuccess: close });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submit(false);
  };

  const unchanged =
    isEdit &&
    trimmedName === institution.name &&
    trimmedInitials === institution.initials &&
    uf === institution.uf &&
    isInHouse === institution.isInHouse;

  const canSubmit =
    !!trimmedName &&
    !!trimmedInitials &&
    !!uf &&
    !unchanged &&
    !mutation.isPending;

  // Renomear sigla ou nome muda o contrato de planilhas JÁ preparadas: as duas
  // barram o lote na importação. Avisa, não bloqueia — quem cadastrou errado
  // tem de poder corrigir, e empurrar a correção para a duplicata é o problema
  // pior.
  const renameWarning =
    isEdit &&
    institution.examCount > 0 &&
    (trimmedInitials !== institution.initials ||
      trimmedName !== institution.name);

  const duplicateId = readDuplicateId(mutation.error);
  const similar = readSimilar(mutation.error);

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? 'Editar instituição' : 'Cadastrar instituição'}
      description={
        isEdit
          ? 'Sigla, nome e UF formam o rótulo que a planilha de importação usa.'
          : 'A instituição é quem aplica a prova. A sigla e o nome juntos são o que a planilha de importação tem de dizer.'
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome"
          value={name}
          onChange={(e) => setKeyName(e.target.value)}
          placeholder="ex.: Universidade Estadual de Campinas"
          required
          autoFocus
        />
        <div className="flex gap-3">
          <Input
            label="Sigla"
            value={initials}
            // Maiúscula ao digitar: o índice já ignora a caixa, e isto só
            // mantém a sigla gravada no mesmo formato das outras.
            onChange={(e) => setKeyInitials(e.target.value.toUpperCase())}
            placeholder="ex.: UNICAMP"
            required
          />
          <Select
            label="UF"
            value={uf}
            onChange={(e) => setKeyUf(e.target.value as Uf)}
            placeholder="UF"
            options={UF_OPTIONS}
            required
          />
        </div>

        <Switch
          label="Conteúdo próprio (Aristo)"
          hint="As provas desta instituição são produzidas pela casa, não aplicadas por uma banca de fora. Hoje não altera nenhum número — serve para poder separar conteúdo próprio da incidência geral quando essa decisão sair."
          checked={isInHouse}
          onChange={setIsInHouse}
        />
        <p className="text-(--color-text-secondary) text-xs">
          Marque só se as provas desta instituição são produzidas pela casa.
          Hoje o campo não altera nenhum número.
        </p>

        {/* O rótulo do CSV, montado ao vivo: é a célula que a planilha precisa. */}
        <div className="border-(--color-border) bg-(--color-bg-secondary) rounded-lg border p-3">
          <p className="text-(--color-text-secondary) text-xs">
            No CSV de importação, esta instituição é:
          </p>
          <Mono>
            ({trimmedInitials || 'SIGLA'}-{uf || 'UF'}){' '}
            {trimmedName || 'Nome da instituição'}
          </Mono>
        </div>

        {renameWarning && (
          <p className="text-sm text-amber-600">
            {institution.examCount}{' '}
            {institution.examCount === 1 ? 'prova usa' : 'provas usam'} esta
            instituição. Planilhas já preparadas com a sigla ou o nome antigos
            vão falhar na validação da importação.
          </p>
        )}

        {candidates.length > 0 && (
          <div className="border-(--color-border) rounded-lg border p-3">
            <p className="text-(--color-text-primary) text-sm font-medium">
              Já existe cadastro com a sigla {lookupInitials.toUpperCase()}
            </p>
            <p className="text-(--color-text-secondary) mt-0.5 text-xs">
              Confira se não é uma destas com o nome escrito de outro jeito —
              cadastrar uma segunda linha parte a incidência da mesma
              instituição em dois registros.
            </p>
            <ul className="mt-2 space-y-1">
              {candidates.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center gap-2 text-xs"
                >
                  <Mono>
                    {row.initials}-{row.uf}
                  </Mono>
                  <span className="text-(--color-text-primary)">
                    {row.name}
                  </span>
                  {row.deletedAt ? (
                    <Badge variant="warning">Arquivada</Badge>
                  ) : (
                    <Badge variant="success">Ativa</Badge>
                  )}
                  <span className="text-(--color-text-secondary)">
                    {row.examCount} {row.examCount === 1 ? 'prova' : 'provas'}
                  </span>
                  {onOpenExisting && (
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label={`Abrir ${row.initials}-${row.uf} ${row.name}`}
                      onClick={() => onOpenExisting(row.id, row.initials)}
                    >
                      Abrir
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {mutation.error != null && (
          <div className="space-y-1">
            <p className="text-sm text-red-600">
              {translateInstitutionError(
                mutation.error,
                isEdit ? 'Falha ao salvar' : 'Falha ao cadastrar instituição',
              )}
            </p>
            {duplicateId && onOpenExisting && (
              <Button
                type="button"
                variant="ghost"
                // O 409 de duplicata casa a chave inteira, então a sigla
                // digitada é a da linha existente.
                onClick={() => onOpenExisting(duplicateId, trimmedInitials)}
              >
                Abrir a instituição que já existe
              </Button>
            )}
            {similar.length > 0 && (
              <>
                <ul className="space-y-1">
                  {similar.map((row) => (
                    <li
                      key={row.id}
                      className="flex flex-wrap items-center gap-2 text-xs"
                    >
                      <Mono>
                        {row.initials}-{row.uf}
                      </Mono>
                      <span className="text-(--color-text-primary)">
                        {row.name}
                      </span>
                      {onOpenExisting && (
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={`Abrir ${row.initials}-${row.uf} ${row.name}`}
                          onClick={() => onOpenExisting(row.id, row.initials)}
                        >
                          Abrir
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={mutation.isPending}
                  onClick={() => submit(true)}
                >
                  {isEdit ? 'Salvar mesmo assim' : 'Cadastrar mesmo assim'}
                </Button>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {mutation.isPending ? 'Salvando…' : isEdit ? 'Salvar' : 'Cadastrar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** UF vinda de fora (deep link) só é aceita se for uma das 27. */
function pickUf(value: string | undefined): Uf | '' {
  const upper = (value ?? '').toUpperCase();
  return (UFS as readonly string[]).includes(upper) ? (upper as Uf) : '';
}

/**
 * As duas colisões de cadastro em texto de gente. Elas dizem coisas diferentes
 * e levam a ações diferentes, então não colapsam numa mensagem só.
 */
export function translateInstitutionError(
  err: unknown,
  fallback: string,
): string {
  switch (readErrorCode(err)) {
    case 'INSTITUTION_ALREADY_EXISTS':
      return 'Já existe uma instituição ativa com esta sigla, UF e nome. Abra a que existe em vez de criar uma segunda.';
    case 'INSTITUTION_DUPLICATE':
      return 'Esta instituição já existe — mesma sigla, UF e nome. Abra a linha existente (restaure, se estiver arquivada) em vez de criar uma segunda.';
    case 'INSTITUTION_SIMILAR_EXISTS':
      return 'Já existe instituição com esta sigla e UF, com outro nome. Confira se não é uma destas escrita de outro jeito; se for outra instituição, confirme.';
    case 'INSTITUTION_IN_USE': {
      const count = readErrorDetail<number>(err, 'examCount');
      return count === undefined
        ? 'Não é possível arquivar: há provas apontando para esta instituição.'
        : `Não é possível arquivar: ${count} ${count === 1 ? 'prova aponta' : 'provas apontam'} para esta instituição. Edite o cadastro ou mescle na linha certa.`;
    }
    case 'INSTITUTION_NOT_DELETED':
      return 'Esta instituição não está arquivada.';
    case 'INSTITUTION_MERGE_TOO_LARGE': {
      const count = readErrorDetail<number>(err, 'examCount');
      const max = readErrorDetail<number>(err, 'maxExamCount');
      return count === undefined || max === undefined
        ? 'Esta instituição tem provas demais para uma mescla só.'
        : `Esta instituição tem ${count} provas, acima do limite de ${max} por mescla. Fale com quem cuida do backend: acima desse número a mescla precisa rodar em lote.`;
    }
    // `INSTITUTION_MERGE_CONFLICT` NÃO entra aqui: a lista de colisões do
    // `details` é acionável (cada linha é uma prova para abrir), então ela é
    // renderizada como tabela pelo `MergeInstitutionModal` em vez de virar uma
    // frase. Ver `readMergeCollisions`.
    case 'INSTITUTION_NOT_FOUND':
      return 'Instituição não encontrada. Se está mesclando, a instituição que fica precisa estar ativa — uma arquivada não recebe provas.';
    default:
      return getApiErrorMessage(err, fallback);
  }
}

/** As parecidas que o 409 `INSTITUTION_SIMILAR_EXISTS` carrega. */
function readSimilar(err: unknown): SimilarInstitution[] {
  if (readErrorCode(err) !== 'INSTITUTION_SIMILAR_EXISTS') return [];
  return (
    readErrorDetail<SimilarInstitution[]>(err, 'similarInstitutions') ?? []
  );
}

/** O id que o 409 `INSTITUTION_DUPLICATE` carrega — é o atalho para abrir a linha. */
function readDuplicateId(err: unknown): string | undefined {
  if (readErrorCode(err) !== 'INSTITUTION_DUPLICATE') return undefined;
  return readErrorDetail<string>(err, 'existingInstitutionId');
}
